import CodeOutlined from '@mui/icons-material/CodeOutlined';
import { Box, Paper, Stack, Typography } from '@mui/material';
import MUIModal from '@mui/material/Modal';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import StyledFilters from '../../../components/dashboard/MultiAgentDashboard/StyledFilters';
import AutocompleteInput from '../../../components/shared/AutoCompleteInput/AutoCompleteInput';
import BarChart from '../../../components/shared/BarChart/BarChart';
import Button from '../../../components/shared/Button/Button';
import ChatAgentProfileAvatar from '../../../components/shared/ChatAgentProfileAvatar/ChatAgentProfileAvatar';
import ChatOverAllMetrics from '../../../components/shared/ChatOverAllMetrics/ChatOverAllMetrics';
import CodeSnippet from '../../../components/shared/CodeSnippet/CodeSnippet';
import ConversationsTable from '../../../components/shared/ConversationsTable/ConversationsTable';
import DateRangePicker from '../../../components/shared/DateRangePicker/DateRangePicker';
import Input from '../../../components/shared/Input/Input';
import LiveSyncStatus from '../../../components/shared/LiveSyncToggle/LiveSyncStatus';
import LiveSyncStoppedModal from '../../../components/shared/LiveSyncToggle/LiveSyncStoppedModal';
import LiveSyncToggle from '../../../components/shared/LiveSyncToggle/LiveSyncToggle';
import Loading from '../../../components/shared/Loading/Loading';
import StyledModal from '../../../components/shared/Modal/StyledModal';
import Page from '../../../components/shared/Page/Page';
import SecondaryFooter from '../../../components/shared/SecondaryFooter/SecondaryFooter';
import Text from '../../../components/shared/Text/Text';
import ToolTip from '../../../components/shared/ToolTip/ToolTip';
import { CHAT_WIDGET_PUBLIC_KEY } from '../../../config/appConfig';
import { useSingleChatAgentDashboard } from '../../../hooks/queries/useSingleChatAgentDashboard';
import useAuth from '../../../hooks/useAuth';
import useGetAgentById from '../../../hooks/useGetAgentById';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { useLiveSync } from '../../../hooks/useLiveSync';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentType } from '../../../interfaces/Agent';
import { updateChatAgent } from '../../../services/chatAgentService';
import HorizontalStack from '../../../styles/HorizontalStack';
import StyledForm from '../../../styles/StyledForm';
import StyledFormItem from '../../../styles/StyledFormItem';

type FormValues = {
  name: string;
  header_text: string;
  bubble_text: string;
  start_message: string;
  prompt_suggestions: string[];
};

const ShowChatAgent: React.FC = () => {
  const methods = useForm<FormValues>();
  const { agent, isLoading, shouldNavigate, refetch } = useGetAgentById(
    AgentType.CHAT
  );
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  const {
    chatMetrics,
    conversationsPerMonth,
    conversationsPerDay,
    conversations,
    dateRange,
    serverPaging,
    isRefreshingMetrics,
    refetch: refetchDashboard,
    isLoading: isLoadingDashboard,
    isFetching: isFetchingDashboard,
    error: dashboardError,
    setDateRange,
  } = useSingleChatAgentDashboard(
    agent?.company_id ?? currentUser?.company_id,
    agent?.user_id ?? currentUser?.id,
    agent?.id
  );
  const liveSync = useLiveSync({
    refetch: refetchDashboard,
    isUpdating: isFetchingDashboard,
  });
  const displayMessage = useSnackBar();
  useErrorToast(dashboardError);
  const [codeSnippetModalOpen, setCodeSnippetModalOpen] = useState(false);

  useEffect(() => {
    if (!agent) return;

    methods.reset({
      name: agent.name ?? '',
      header_text: agent.custom_initialization?.header_text ?? '',
      bubble_text:
        agent.custom_initialization?.labels_to_translate?.bubble_text ?? '',
      start_message: agent.start_message ?? '',
      prompt_suggestions: agent.custom_initialization?.prompt_suggestions ?? [],
    });
  }, [agent, methods]);

  const updateAgentDetails = async (agentData: FormValues) => {
    try {
      await updateChatAgent(agent?.id ?? '', agentData);
      displayMessage(t('Chat agent details were updated'), 'success');

      // Refetch the updated chat agent details
      await refetch();
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message);
        displayMessage(err.message, 'error');
      }
    }
  };

  if (shouldNavigate) {
    return <Navigate to="/dashboard" />;
  }

  if (isLoading || !agent) return <Loading />;

  return (
    <Page>
      <HorizontalStack gap="1.5rem" alignItems="flex-start">
        {/* Left column */}
        <Stack direction="column" width={{ xs: '100%', md: '32%' }} gap={2}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <ChatAgentProfileAvatar
              agentId={agent.id}
              avatarUrl={agent.custom_initialization?.header_avatar_url}
              agentName={agent.name}
              agentStatus={agent.status}
            />
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setCodeSnippetModalOpen(true)}
              style={{ marginTop: '20px' }}
            >
              <CodeOutlined sx={{ marginRight: '0.5rem' }} />
              {t('View Code Snippet')}
            </Button>
            <MUIModal
              open={codeSnippetModalOpen}
              onClose={() => setCodeSnippetModalOpen(false)}
            >
              <StyledModal sx={{ width: { sm: '600px', md: '800px' } }}>
                <CodeSnippet
                  appId={agent.id}
                  publicKey={CHAT_WIDGET_PUBLIC_KEY}
                />
              </StyledModal>
            </MUIModal>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
              {t('Chat settings')}
            </Typography>
            <FormProvider {...methods}>
              <StyledForm
                gap="unset"
                marginTop="0"
                onSubmit={methods.handleSubmit(updateAgentDetails)}
              >
                <StyledFormItem>
                  <Text type="label">
                    {t('Chat Name')}
                    <ToolTip title={t('The display name of the chat agent.')} />
                  </Text>
                  <Input name="name" required={true} />
                </StyledFormItem>

                <StyledFormItem>
                  <Text type="label">
                    {t('Chat Title')}
                    <ToolTip
                      title={t(
                        'The title displayed in the chat widget header.'
                      )}
                    />
                  </Text>
                  <Input name="header_text" required={true} />
                </StyledFormItem>

                <StyledFormItem>
                  <Text type="label">
                    {t('Chat Introduction')}
                    <ToolTip
                      title={t(
                        'The introductory message shown to users when they open the chat.'
                      )}
                    />
                  </Text>
                  <Input name="bubble_text" />
                </StyledFormItem>

                <StyledFormItem>
                  <Text type="label">
                    {t('Prompt Suggestions')}
                    <ToolTip
                      title={t(
                        'Suggested prompts that users can click to start a conversation.'
                      )}
                    />
                  </Text>
                  <AutocompleteInput
                    name="prompt_suggestions"
                    options={[]}
                    initialValue={
                      agent?.custom_initialization?.prompt_suggestions ?? []
                    }
                    customOptionEnabled
                  />
                </StyledFormItem>

                <Button type="submit" disabled={!methods.formState.isDirty}>
                  {t('Save')}
                </Button>
              </StyledForm>
            </FormProvider>
          </Paper>
        </Stack>
        {/* Right column */}
        <Stack width={{ xs: '100%', md: '68%' }} direction="column">
          <StyledFilters>
            <DateRangePicker
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={setDateRange}
            />
            <LiveSyncToggle
              enabled={liveSync.isEnabled}
              onChange={liveSync.setIsEnabled}
              disabled={isLoadingDashboard}
            />
          </StyledFilters>
          {liveSync.isEnabled && (
            <LiveSyncStatus
              isUpdating={liveSync.isUpdating}
              secondsSinceUpdate={liveSync.secondsSinceUpdate}
            />
          )}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <ChatOverAllMetrics
              {...chatMetrics}
              isRefreshing={isRefreshingMetrics}
            />
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              mt: 2,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <BarChart
              agentType={AgentType.CHAT}
              chatPerMonth={conversationsPerMonth}
              perDay={conversationsPerDay}
              dateRange={dateRange}
            />
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              mt: 2,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography component="h3" variant="h6" sx={{ m: 0 }}>
                {t('Conversations')}
              </Typography>
            </Box>
            <ConversationsTable
              conversations={conversations}
              agentType={AgentType.CHAT}
              serverPaging={serverPaging}
            />
          </Paper>
        </Stack>
      </HorizontalStack>
      <LiveSyncStoppedModal
        open={liveSync.showLimitModal}
        setOpen={liveSync.setShowLimitModal}
      />
      <SecondaryFooter />
    </Page>
  );
};

export default ShowChatAgent;
